import { MenuState, Store } from "store/types";
import { NavItemType } from "../../../../../menu-items/types";

import { forwardRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Avatar,
  Chip,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography
} from "@mui/material";

import { useTheme } from "@mui/material/styles";
import { activeItem } from "store/reducers/menu";
import { useTranslation } from "../../../../../contexts/TranslationContext";

// ==============================|| NAVIGATION - LIST ITEM ||============================== //

type NavItemProps = {
  item: NavItemType;
  level: number;
};

const NavItem = ({ item, level }: NavItemProps) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { translate } = useTranslation();

  const { drawerOpen, openItem } = useSelector<Store, MenuState>(
    state => state.menu
  );

  let itemTarget = "_self";
  if (item.target) {
    itemTarget = "_blank";
  }

  let listItemProps: {} = {
    component: forwardRef<HTMLAnchorElement>((props, ref) => {
      if (item.url) {
        return (
          <Link
            ref={ref}
            {...props}
            to={item.url}
            target={itemTarget}
          />
        );
      }
      return <Typography component="span" ref={ref} {...props} />;
    })
  };

  if (item?.external) {
    listItemProps = { component: "a", href: item.url, target: itemTarget };
  }

  const itemHandler = (clickedItem: NavItemType) => {
    if (clickedItem.onClick) {
      clickedItem.onClick();
    } else {
      dispatch(activeItem({ openItem: [clickedItem.id] }));
    }
  };

  const Icon = item.icon;
  const itemIcon = item.icon ? (
    <Icon style={{ fontSize: drawerOpen ? "1rem" : "1.25rem" }} />
  ) : null;

  const isSelected = openItem.findIndex(id => id === item.id) > -1;

  // active menu item on page load
  useEffect(() => {
    if (item.url) {
      if (pathname.includes(item.url)) {
        dispatch(activeItem({ openItem: [item.id] }));
      }
    }
    // eslint-disable-next-line
  }, [pathname]);

  const textColor = "text.primary";
  const iconSelectedColor = "primary.main";

  return (
    <ListItemButton
      {...listItemProps}
      disabled={item.disabled}
      onClick={() => itemHandler(item)}
      selected={isSelected}
      title={translate(item.title)}
      sx={{
        zIndex: 1201,
        pl: drawerOpen ? `${level * 28}px` : 2,
        py: 0.5,
        ...(drawerOpen && {
          "&:hover": {
            bgcolor: "primary.lighter"
          },
          "&.Mui-selected": {
            bgcolor: "primary.lighter",
            borderRight: `2px solid ${theme.palette.primary.main}`,
            color: iconSelectedColor,
            "&:hover": {
              color: iconSelectedColor,
              bgcolor: "primary.lighter"
            }
          }
        }),
        ...(!drawerOpen && {
          "&:hover": {
            bgcolor: "primary.lighter"
          },
          "&.Mui-selected": {
            "&:hover": {
              bgcolor: "primary.lighter"
            },
            bgcolor: "primary.lighter",
            borderRight: `2px solid ${theme.palette.primary.main}`
          }
        })
      }}>
      {itemIcon && (
        <ListItemIcon
          sx={{
            width: 28,
            color: isSelected ? iconSelectedColor : textColor,
            ...(!drawerOpen && {
              alignItems: "center",
              justifyContent: "center",
              "&:hover": {
                bgcolor: "primary.lighter"
              }
            }),
            ...(!drawerOpen &&
              isSelected && {
                bgcolor: "primary.lighter",
                "&:hover": {
                  bgcolor: "primary.lighter"
                }
              })
          }}>
          {itemIcon}
        </ListItemIcon>
      )}
      <ListItemText
        primary={
          <Typography
            variant="h6"
            sx={{
              color: isSelected
                ? iconSelectedColor
                : textColor,
              ml: drawerOpen ? 0 : 60
            }}>
            {translate(item.title)}
          </Typography>
        }
      />
      {(drawerOpen || (!drawerOpen && level !== 1)) && item.chip && (
        <Chip
          color={item.chip.color}
          variant={item.chip.variant}
          size={item.chip.size}
          label={item.chip.label}
          avatar={
            item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>
          }
        />
      )}
    </ListItemButton>
  );
};

export default NavItem;
